import { Box, Grid, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { InfoCard, SlidingPanelContent } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Email, Link, Loader, PhoneNumber } from 'core/elements';
import { FileHelper } from 'core/helpers';
import { IInfoCardLine } from 'core/types';
import { IPartManufacturer } from 'parts/types';
import { Color, Level, Size } from 'theme/enums';

interface IProps {
  manufacturer?: IPartManufacturer | null;
  fullWidth?: boolean;
  isLoading?: boolean;
}

export const Manufacturer = ({ manufacturer, fullWidth = true, isLoading = false }: IProps) => {
  const { t } = useTranslation();

  const logoSrc = FileHelper.getDisplayUrl(manufacturer?.logoFileId);

  const hasAnyData = Boolean(
    manufacturer?.name ||
      manufacturer?.website ||
      manufacturer?.mainPhone ||
      manufacturer?.email ||
      manufacturer?.logoFileId
  );

  const items: IInfoCardLine[] = hasAnyData
    ? [
        {
          label: t('labelManufacturer'),
          value: manufacturer?.name || DEFAULT_PLACEHOLDER,
        },
        {
          label: t('labelUrl'),
          value: manufacturer?.website ? (
            <Link
              href={manufacturer.website}
              target="_blank"
              rel="noopener noreferrer"
              title={manufacturer.website}
            >
              {manufacturer.website}
            </Link>
          ) : (
            DEFAULT_PLACEHOLDER
          ),
        },
        {
          label: t('labelPhone'),
          value: <PhoneNumber phone={manufacturer?.mainPhone} />,
        },
        {
          label: t('labelEmail'),
          value: <Email email={manufacturer?.email} />,
        },
      ]
    : [];

  return (
    <SlidingPanelContent title={t('titleManufacturer')} fullWidth={fullWidth}>
      {isLoading && (
        <Loader
          size={Size.SM}
          style={{
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            width: 'fit-content',
            height: 'fit-content',
          }}
        />
      )}

      {!isLoading && !hasAnyData && (
        <Typography
          color={Color.NEUTRAL}
          level={Level.TITLE_SM}
          fontStyle="italic"
          sx={{ padding: 1 }}
        >
          {t('messageNoManufacturer')}
        </Typography>
      )}

      {!isLoading && hasAnyData && (
        <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
          <Grid xs={12} md={6}>
            <InfoCard items={items} />
          </Grid>

          {logoSrc && (
            <Grid xs={12} md={6}>
              <Box
                component="img"
                src={logoSrc}
                alt={manufacturer?.name ?? ''}
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            </Grid>
          )}
        </Grid>
      )}
    </SlidingPanelContent>
  );
};
